import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Plus, ShoppingBasket, Trash2, TrendingUp } from 'lucide-react';
import {
  addASOKeywordToBasket,
  applyASOBasket,
  getASOAppCoverage,
  getASOBasket,
  removeASOKeywordFromBasket,
  type ASOBasketResponse,
  type ASOCoverageResponse,
  type ASOTermRow,
} from '../api/client';
import { AppError } from '../api/request';
import { useApplication } from '../context/ApplicationContext';
import { useError } from '../context/ErrorContext';
import AppPageLayout from './AppPageLayout';
import { countryFlag, DeltaBadge, formatGenreLabel, PopularityBar, Sparkline } from './asoUI';

/* Uygulama bazlı ASO gap analizi.
 *
 * Akışın kapanış adımı: pazar verisi burada kullanıcının kendi keyword'leriyle karşılaştırılır ve
 * "ne yapmalıyım" sorusuna dönüşür. Seçilen terimler önce sepete birikir, sonra tek seferde
 * versiyonun keyword alanına uygulanır — App Store'un 15 terim / 100 karakter sınırı yüzünden
 * ekleme kararı toplu verilmeli. */
const AppASOPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const { t } = useTranslation();
  const { showError } = useError();
  const { app: contextApp, loading, error: contextError } = useApplication();
  const app = appId && contextApp?.id === appId && contextApp.type === 'mobil_app' ? contextApp : null;

  const [coverage, setCoverage] = useState<ASOCoverageResponse | null>(null);
  const [basket, setBasket] = useState<ASOBasketResponse | null>(null);
  const [locale, setLocale] = useState('');
  const [genre, setGenre] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    if (!appId) return;
    setLoadingData(true);
    try {
      const cov = await getASOAppCoverage(appId, {
        locale: locale || undefined,
        genre: genre || undefined,
      });
      setCoverage(cov);
      if (!locale) setLocale(cov.locale);
      if (!genre) setGenre(cov.genre);
      const b = await getASOBasket(appId, { locale: cov.locale });
      setBasket(b);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.loadError'));
    } finally {
      setLoadingData(false);
    }
  }, [appId, locale, genre, showError, t]);

  useEffect(() => {
    if (app) void load();
  }, [app, load]);

  const handleAdd = async (row: ASOTermRow) => {
    if (!appId || !coverage) return;
    try {
      await addASOKeywordToBasket(appId, {
        locale: coverage.locale,
        searchTerm: row.searchTerm,
        countryOrRegion: coverage.countryOrRegion,
        genre: coverage.genre,
        popularity: row.popularity,
        rankInGenre: row.rankInGenre,
      });
      setBasket(await getASOBasket(appId, { locale: coverage.locale }));
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.basketError'));
    }
  };

  const handleRemove = async (suggestionId: string) => {
    if (!appId || !coverage) return;
    try {
      await removeASOKeywordFromBasket(appId, suggestionId);
      setBasket(await getASOBasket(appId, { locale: coverage.locale }));
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.basketError'));
    }
  };

  const handleApply = async () => {
    if (!appId || !coverage) return;
    setApplying(true);
    try {
      const res = await applyASOBasket(appId, { locale: coverage.locale });
      setBasket(res);
      await load();
      showError(t('aso.applySuccess'));
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.applyError'));
    } finally {
      setApplying(false);
    }
  };

  const inBasket = new Set((basket?.items ?? []).map(i => i.searchTerm.toLowerCase()));

  return (
    <AppPageLayout app={app} loading={loading} error={contextError} pageTitle={t('aso.appPageTitle')}>
      {app && loadingData && !coverage && (
        <div className="text-center text-gray-500 text-sm py-8">{t('common.loading')}</div>
      )}

      {app && coverage && !coverage.period && (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
          <TrendingUp className="w-8 h-8 mx-auto text-gray-400 mb-3" />
          <p className="text-sm font-medium text-gray-900 dark:text-white">{t('aso.emptyTitle')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('aso.emptyBody')}</p>
        </div>
      )}

      {app && coverage && coverage.period && (
        <div className="space-y-4">
          {/* Bağlam: hangi dil, hangi ülke, hangi genre karşılaştırılıyor */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <select
              value={locale}
              onChange={e => setLocale(e.target.value)}
              className="h-8 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              <option value={coverage.locale}>{coverage.locale}</option>
            </select>
            <span className="text-gray-500 dark:text-gray-400">
              {countryFlag(coverage.countryOrRegion)} {coverage.countryOrRegion}
              {coverage.tier ? ` · ${t(`aso.tier.${coverage.tier}`)}` : ''}
            </span>
            <select
              value={genre}
              onChange={e => setGenre(e.target.value)}
              className="h-8 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              {coverage.availableGenres.map(g => (
                <option key={g} value={g}>
                  {formatGenreLabel(g)}
                </option>
              ))}
            </select>
            {coverage.genreAutoResolved && (
              <span className="text-xs text-gray-400">{t('aso.genreAutoResolved')}</span>
            )}
            <span className="ml-auto text-xs text-gray-400 tabular-nums">{coverage.period}</span>
          </div>

          {/* Özet kartlar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard
              label={t('aso.metricCoverage')}
              value={`${coverage.keywordCount}/${coverage.keywordLimit}`}
            />
            <MetricCard
              label={t('aso.metricChars')}
              value={`${coverage.keywordCharCount}/${coverage.keywordCharLimit}`}
              warn={coverage.keywordCharCount > coverage.keywordCharLimit}
            />
            <MetricCard label={t('aso.metricMissed')} value={String(coverage.missed.length)} accent="amber" />
            <MetricCard label={t('aso.metricDead')} value={String(coverage.deadWeight.length)} accent="red" />
          </div>

          {/* Öneri sepeti */}
          {basket && basket.items.length > 0 && (
            <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ShoppingBasket className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('aso.basketTitle', { count: basket.items.length })}
                </p>
                <span
                  className={`ml-auto text-xs tabular-nums ${
                    basket.exceedsLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {basket.resultingCount}/{basket.keywordLimit} · {basket.resultingCharCount}/
                  {basket.keywordCharLimit}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {basket.items.map(item => (
                  <span
                    key={item.id}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200"
                  >
                    {item.searchTerm}
                    <button
                      type="button"
                      onClick={() => handleRemove(item.id)}
                      aria-label={t('aso.removeFromBasket')}
                      className="p-0.5 rounded text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              {basket.exceedsLimit && (
                <p className="text-xs text-red-600 dark:text-red-400">{t('aso.basketExceeds')}</p>
              )}

              <button
                type="button"
                onClick={handleApply}
                disabled={applying || basket.exceedsLimit}
                className="inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                {t('aso.applyToVersion')}
              </button>
            </div>
          )}

          <BucketSection
            title={t('aso.bucketMissed')}
            hint={t('aso.bucketMissedHint')}
            rows={coverage.missed}
            actionable
            inBasket={inBasket}
            onAdd={handleAdd}
          />
          <BucketSection
            title={t('aso.bucketDead')}
            hint={t('aso.bucketDeadHint')}
            rows={coverage.deadWeight}
          />
          <BucketSection
            title={t('aso.bucketWorking')}
            hint={t('aso.bucketWorkingHint')}
            rows={coverage.working}
          />
        </div>
      )}
    </AppPageLayout>
  );
};

const MetricCard: React.FC<{ label: string; value: string; accent?: 'amber' | 'red'; warn?: boolean }> = ({
  label,
  value,
  accent,
  warn,
}) => {
  const color =
    warn || accent === 'red'
      ? 'text-red-600 dark:text-red-400'
      : accent === 'amber'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-xl font-medium mt-1 tabular-nums ${color}`}>{value}</p>
    </div>
  );
};

const BucketSection: React.FC<{
  title: string;
  hint: string;
  rows: ASOTermRow[];
  actionable?: boolean;
  inBasket?: Set<string>;
  onAdd?: (row: ASOTermRow) => void;
}> = ({ title, hint, rows, actionable, inBasket, onAdd }) => {
  const { t } = useTranslation();
  if (!rows.length) return null;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-medium text-gray-900 dark:text-white">
          {title} <span className="text-gray-400 tabular-nums">{rows.length}</span>
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
        {rows.slice(0, 30).map(row => (
          <div key={row.searchTerm} className="flex items-center gap-3 px-4 py-2.5">
            <span className="text-sm text-gray-900 dark:text-gray-100 flex-1 min-w-0 truncate">
              {row.searchTerm}
            </span>
            <PopularityBar value={row.popularity} className="w-24 flex-shrink-0" />
            <span className="w-14 text-right flex-shrink-0">
              <DeltaBadge delta={row.popularityDelta} isNew={row.isNew} />
            </span>
            <span className="hidden sm:block flex-shrink-0">
              <Sparkline points={row.sparkline} delta={row.popularityDelta} />
            </span>
            {actionable && onAdd && (
              <button
                type="button"
                onClick={() => onAdd(row)}
                disabled={inBasket?.has(row.searchTerm.toLowerCase())}
                aria-label={t('aso.addToBasket')}
                className="p-1 rounded text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-40 flex-shrink-0"
              >
                {inBasket?.has(row.searchTerm.toLowerCase()) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default AppASOPage;
